/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlinx.serialization.Serializable

/**
 * Keyring capable of holding [UniversalKey] instances (serializable keys of different types)
 * associated with a set of string tags. Rings are used to decrypt [Container], to store
 * and find keys, etc.
 *
 * It is _immutable_ so it is safe to share id with no precautions on possible RC. Every
 * function that modifies the ring returns a _new instance_ of it.
 *
 * __important__ serialized keyring is not protected and can disclose keys. Encrypt it after
 * serializing before sending over the network or store on media.
 */
@Serializable
class UniversalRing(
    val keyWithTags: Map<UniversalKey, Set<String>>,
) {
    constructor(vararg keys: UniversalKey) : this(keys.associate { it to setOf() })

    constructor(vararg keyTags: Pair<UniversalKey, String>)
            : this(keyTags.associate { it.first to setOf(it.second) })

    /**
     * Only decrypting keys. This is a shortcut for [keysOfType]:
     * `keysOfType<DecryptingKey>()`.
     */
    val decryptingKeys: Set<DecryptingKey> by lazy { keysOfType<DecryptingKey>() }
    
    /**
     * Select all keys of the specified type. To select all keys, use [allKeys].
     */
    inline fun <reified T: KeyInstance> keysOfType(): Set<T> =
        allKeys.mapNotNull { it as? T }.toSet()

    val allKeys: Set<UniversalKey> by lazy { keyWithTags.keys }

    /**
     * Find a key of the specified type that matches the id. In general, you require key implementations like
     * [DecryptingSecretKey], [EncryptingPublicKey], [VerifyingPublicKey], [SigningSecretKey] and [SymmetricKey],
     * or just key interfaces: [EncryptingKey], [DecryptingKey], [SigningKey] and [VerifyingKey].
     *
     * Note that key interfaces are not serializable as for now, you should try to cast to a serializable
     * base.
     *
     * Please avoid selecting parameters that make possible to pick more than one key, it will cause an exception.
     *
     * @return found key by id of the specified type or null
     * @throws IllegalArgumentException if there is more than one keys matching the criteria
     */
    inline fun <reified T: KeyInstance> findKey(id: KeyId): T? {
        val kk = allKeys.filter { it is T && it.id == id }
        if( kk.size > 1 )
            throw IllegalArgumentException(
                "ambiguous type selector ${T::class::simpleName}: ${kk.size} instances found, at most 1 allowed"
            )
        return kk.firstOrNull() as T?
    }

    /**
     * Get all keys for the specified id (normally it could be 0, 1 or 2). See [KeyId] about
     * matching id keys.
     */
    fun findById(id: KeyId): List<UniversalKey> = allKeys.filter { it.id == id }

    @Deprecated("please replace", replaceWith = ReplaceWith("findById"))
    fun keysById(id: KeyId) = findById(id)

    /**
     * Return sequence of keys that have at least one of the [tags]
     */
    fun findByTags(vararg tags: String) = sequence {
        for (e in keyWithTags.entries) {
            if (tags.any { it in e.value }) yield(e.key)
        }
    }

    @Deprecated("please replace", replaceWith = ReplaceWith("findByTag"))
    fun keysByTag(vararg tags: String) = findByTags(*tags)

    /**
     * Get the first key of the specified type having the [tag]
     */
    inline fun <reified T> keyByTag(tag: String) = findByTags(tag).first { it is T }

    /**
     * Get keys of the specified type having any of the specified tags associated.
     */
    @Suppress("unused")
    inline fun <reified T> keysByAnyTag(vararg tags: String): Sequence<UniversalKey> =
        findByTags(*tags).filter { it is T }

    /**
     * Get all keys with a given id. Note that _matching keys_ have the same id, see [KeyId] for more.
     */
    operator fun get(keyId: KeyId): Collection<UniversalKey> = allKeys.filter { it.id == keyId }

    fun getTags(key: UniversalKey): Set<String>? = keyWithTags[key]

    operator fun contains(element: UniversalKey): Boolean = element in keyWithTags

    /**
     * Add a key if not already in a ring, otherwise return existing ring.
     */
    operator fun plus(key: UniversalKey): UniversalRing =
        if (key in this) this else UniversalRing(keyWithTags + (key to setOf()))

    /**
     * Add a key and tags to the ring. If the key exists, add the tag to it.
     * See also [addTags].
     */
    operator fun plus(keyTag: Pair<UniversalKey, String>): UniversalRing =
        if (keyTag.first in this)
            addTags(keyTag.first, keyTag.second)
        else add(keyTag.first, keyTag.second)

    /**
     * Merge two rings. The result will contain all keys and all tags from
     * both rings.
     */
    operator fun plus(other: UniversalRing): UniversalRing {
        val result = keyWithTags.toMutableMap()
        for (e in other.keyWithTags.entries) {
            result[e.key]?.let {
                result[e.key] = it + e.value
            } ?: run {
                result[e.key] = e.value
            }
        }
        return UniversalRing(result)
    }

    /**
     * Add key and tags to the ring. If the key already exists, tags are merged.
     */
    fun add(key: UniversalKey, vararg tags: String): UniversalRing =
        UniversalRing(keyWithTags + (key to tags.toSet()))

    /**
     * Add key and tags to the ring. If the key already exists, tags are merged.
     */
    @Suppress("unused")
    fun add(key: UniversalKey, tags: Collection<String>): UniversalRing =
        UniversalRing(keyWithTags + (key to tags.toSet()))

    /**
     * Add tags to the key, and add key if it is not in the ring.
     */
    fun addTags(key: UniversalKey, tags: Set<String>): UniversalRing {
        val kt1 = keyWithTags.toMutableMap()
        kt1[key]?.let {
            kt1[key] = it + tags
        } ?: run {
            kt1[key] = tags
        }
        return UniversalRing(kt1)
    }

    /**
     * Add tags to the key, and add key if it is not in the ring.
     */
    fun addTags(key: UniversalKey, vararg tags: String): UniversalRing =
        addTags(key, tags.toSet())

    /**
     * return the ring without [key] if existed, otherwise returns `this`.
     */
    operator fun minus(key: UniversalKey): UniversalRing =
        if (key in this) UniversalRing(keyWithTags.filter { it.key != key }) else this

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UniversalRing) return false
        return keyWithTags == other.keyWithTags
    }

    override fun hashCode(): Int {
        return keyWithTags.hashCode()
    }

    override fun toString(): String {
        val ss = keyWithTags.entries
            .joinToString(",") { "${it.value.joinToString { ":" }}:${it.key}" }
        return "Kr[$ss]"
    }

    infix fun equalKeys(other: UniversalRing): Boolean = allKeys == other.allKeys

    /**
     * Create a collection where no [tags] are specified for the [key]. If the key is not
     * in the collection, it returns this.
     */
    fun removeTags(key: UniversalKey, vararg tags: String): UniversalRing = removeTags(key, tags.toSet())

    /**
     * Create a collection where no [tags] are specified for the [key]. If the key is not
     * in the collection, it returns this.
     */
    fun removeTags(key: UniversalKey, tags: Set<String>): UniversalRing =
        keyWithTags[key]?.let { existingTags ->
            val kt1 = keyWithTags.toMutableMap()
            kt1[key] = existingTags - tags
            UniversalRing(kt1)
        } ?: this

    /**
     * Create string "report" of the ring contents. Note it has no trailing `\n`
     */
    @Suppress("unused")
    fun ls(): String {
        val result = mutableListOf<String>()
        for( e in keyWithTags.entries) {
            result += "${e.key} ${e.value.joinToString(" ")}"
        }
        return result.joinToString("\n")
    }

    companion object {
        val EMPTY = UniversalRing(keyWithTags = emptyMap())

        fun new(): UniversalRing = UniversalRing(keyWithTags = emptyMap())

        /**
         * Join a collection of keyrings together (same as reducing with `+`). Correctly
         * works if there is no keyring (returns [EMPTY]), or only one keyring (returns
         * it with no extra work).
         *
         * Note that for the known number of rings using [plus] is more clear and expressive
         */
        fun join(keyRings: Collection<UniversalRing>): UniversalRing {
            if (keyRings.isEmpty()) return EMPTY
            if (keyRings.size == 1) return keyRings.first()
            return keyRings.reduce { l, r -> l + r }
        }

        /**
         * Convert any keys to [UniversalKey] and form a ring with it.
         * @throws UnsupportedOperationException if [UniversalKey] can't build an instance of the specified class.
         */
        fun from(vararg keys: DecryptingKey): UniversalRing =
            UniversalRing(keys.associate { it as UniversalKey to setOf() } )

        /**
         * Convert any keys to [UniversalKey] and form a ring with it.
         * @throws UnsupportedOperationException if [UniversalKey] can't build an instance of the specified class.
         */
        fun from(vararg keyTags: Pair<UniversalKey,String>): UniversalRing =
            UniversalRing(keyTags.associate {it.first to setOf(it.second) } )
    }

}
